import { ESdChangeReason } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { CreateAction } from "../data-diagram/create-action";

/**
 * Whether a source action is currently being authored *for this tool*.
 *
 * Exported because the host has to know too: the canvas swaps the parameter table out for the editor, and a
 * component cannot tell its parent whether it rendered anything. One predicate, so the two can never disagree and
 * leave the surface either doubled or blank.
 *
 * <b>Why this reads a recorded claim rather than inspecting the editing state.</b> It used to answer the question
 * by looking at `creatingActionsDatasource` and `editingAction`: a database datasource plus an action being created
 * or edited meant "the tool is authoring a source". That is not something the editing state can tell you. The
 * tool's left panel *is* the datasources tree, so a SQL action opened there to be edited on its own looks exactly
 * like one opened to supply a `<TAG>` — the tool captured both, kept its own right panel standing beside an
 * unrelated action editor, and every neighbouring command (edit a table, edit a connection) fell back to the
 * parameter table because it cleared the state this predicate was reading. The claim is made once, where the
 * intent is known: `DatasourceProvider.claimsDocumentParameterSourceAction`.
 */
export const isEditingDocumentParameterSourceAction = (sd: IWithSDProps["sd"]): boolean => {
  return sd?.isEditingDocumentParameterSourceAction === true;
};

/**
 * The action editor, hosted on the Document Parameters tool's canvas (KOA-17254).
 *
 * <b>What this is for.</b> A document parameter's value can only come from a database query, so authoring one of
 * those is part of this tool's own job — it should not cost the user the tool. That is the single exception to the
 * rule that datasource editors live on the data diagram; `DatasourceProvider.enterDatasourceEditingSurface` sends
 * every other command from the sources tree (a connection, a table, session variables, an AI or SAP action) to the
 * datasources tool, because none of them has an editor here and this canvas would otherwise show nothing at all.
 *
 * <b>Why the exception is claimed rather than recognised.</b> The tool's left panel *is* the sources tree, and the
 * tree's menus are the datasources tool's menus. Nothing in the resulting editing state distinguishes a SQL action
 * opened to be edited on its own from one opened to supply a `<TAG>`, so this file used to claim both — see
 * `isEditingDocumentParameterSourceAction` above for what that cost.
 *
 * <b>Why it replaces the table rather than sitting beside it.</b> Editing an action is a full task with its own
 * save and its own unsaved-changes guard. Two savable forms on one surface is the arrangement this tool has already
 * been rebuilt to avoid — which is also why the right panel steps aside while this is up (`ViewPart.RightPanel`).
 */
class DocumentParameterActionEditor extends PureSdComponent<IWithSDProps, {}> {
  render() {
    if (!isEditingDocumentParameterSourceAction(this.props.sd)) return null;

    // The same wrappers the data diagram canvas provides: `CreateAction` is styled against them, and without them
    // it renders unframed and full-bleed.
    return (
      <div className="kuika_datasource">
        <div className="kuika_datasource-functions__wrapper">
          <CreateAction
            datasource={this.props.sd?.creatingActionsDatasource}
            actionToEdit={this.props.sd?.editingAction ? this.props.sd?.editingAction : undefined}
          />
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return changeReason === ESdChangeReason.OtherSetStates || changeReason === ESdChangeReason.DiActionUpdated;
  };
}

const documentParameterActionEditor = withSD<{}>(DocumentParameterActionEditor);
export { documentParameterActionEditor as DocumentParameterActionEditor };
